// libraries.
import { Badge, Button, Card } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

// styles.
import { WrapperIntersections } from '@domains/intersections/components/IntersectionsList/styles';

// api.
import {
  MissingIntersection,
  useGetIntersectionAuditQuery,
} from '@domains/audit-intersections/store/api-slices/auditIntersections.endpoints';

// slices.
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { setMedianZoneCode } from '@domains/median-zones/store/slices/medianZones.slice';
import {
  getAuditIntersectionsRequested,
  setAuditIntersectionsRequested,
  setSelectedMissingIntersection,
} from '@domains/audit-intersections/store/slices/auditIntersections.slice';

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`;

export const AuditIntersectionsTool = () => {
  const dispatch = useDispatch();
  const country = useSelector(getCountry);
  const requested = useSelector(getAuditIntersectionsRequested);

  // El backend recalcula los solapes directo contra MySQL (self-join
  // espacial); el front solo lista lo que devuelve, sin re-validar geometria.
  const { data, isFetching, isError, refetch } = useGetIntersectionAuditQuery(
    { country },
    { skip: !requested }
  );

  const summary = data?.summary;
  const missing = data?.missing ?? [];
  const stale = data?.stale ?? [];
  const duplicates = data?.duplicates ?? [];

  const handleAudit = () => {
    if (!requested) {
      dispatch(setAuditIntersectionsRequested(true));
    } else {
      refetch();
    }
  };

  // Resalta el par en el mapa (lo hace AuditSelectedIntersection) y carga una
  // de las dos zonas medianas del par para dar contexto — solo una a la vez,
  // porque el mapa solo puede tener una zona mediana activa. Si el par no
  // comparte zona, el otro miembro solo se resalta si ya estaba cargado.
  const handleSelectMissing = (item: MissingIntersection) => {
    dispatch(setSelectedMissingIntersection(item));
    const zone = item.median_zone_min ?? item.median_zone_max;
    if (zone) {
      // String() obligatorio: el backend manda median_zone_id como numero y
      // medianZoneCode se compara con === contra polygon.median_zone_id
      // .toString(). Sin normalizar, los polygons de la zona quedan fuera de
      // las cards y no se pueden editar (aparecen como zona adyacente).
      dispatch(setMedianZoneCode(String(zone)));
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 75px)', overflowY: 'auto' }}>
      <WrapperIntersections>
        <div className="title p-3">Auditar intersecciones</div>
        <div className="px-3 pb-2" style={{ fontSize: '14px' }}>
          Busca pares de polígonos activos que se solapan y no tienen
          intersección guardada, intersecciones guardadas que ya no se
          solapan, y pares duplicados. Haz clic en un par faltante para verlo
          resaltado en el mapa (si su zona ya está cargada).
        </div>
        <div className="px-3 pb-3">
          <Button
            className="btn-habi w-100"
            onClick={handleAudit}
            disabled={isFetching}
          >
            {isFetching ? 'Buscando...' : 'Buscar intersecciones faltantes'}
          </Button>
        </div>

        {isError && (
          <div className="px-3 pb-3" style={{ color: '#DC3545', fontSize: '14px' }}>
            Ocurrió un error al auditar las intersecciones. Intenta de nuevo.
          </div>
        )}

        {!isError && requested && !isFetching && summary && (
          <div className="px-3 pb-3 d-flex flex-wrap gap-2">
            <Badge bg="secondary">
              Solapes geométricos: {summary.solapes_geometricos}
            </Badge>
            <Badge bg="secondary">
              Guardados: {summary.pares_guardados}
            </Badge>
            <Badge bg="warning" text="dark">
              Faltantes: {summary.solapes_no_guardados}
            </Badge>
            <Badge bg="danger">
              Obsoletos: {summary.guardados_sin_solape}
            </Badge>
          </div>
        )}

        {!isError &&
          requested &&
          !isFetching &&
          missing.length === 0 &&
          stale.length === 0 &&
          duplicates.length === 0 && (
            <div className="px-3 pb-3" style={{ fontSize: '14px' }}>
              No se encontraron problemas en las intersecciones. 🎉
            </div>
          )}

        {missing.length > 0 && (
          <div className="px-3 pb-3">
            <div className="pb-2" style={{ fontWeight: 600, fontSize: '14px' }}>
              Faltantes ({missing.length})
            </div>
            <div className="d-flex flex-column gap-2">
              {missing.map(item => (
                <Card
                  key={`${item.p_min}-${item.p_max}`}
                  role="button"
                  onClick={() => handleSelectMissing(item)}
                  style={{ borderLeft: '4px solid #FD7E14', cursor: 'pointer' }}
                >
                  <Card.Body className="p-2">
                    <div className="d-flex align-items-center gap-2">
                      <strong>
                        Polígonos {item.p_min} y {item.p_max}
                      </strong>
                      {Boolean(item.comparten_median_zone) && (
                        <Badge bg="info">Misma zona</Badge>
                      )}
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      IoU: {formatPercent(item.iou)} · % del más chico:{' '}
                      {formatPercent(item.pct_of_smaller)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      Zonas: {item.median_zone_min ?? '—'} /{' '}
                      {item.median_zone_max ?? '—'} — clic para verlo en el
                      mapa
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        )}

        {stale.length > 0 && (
          <div className="px-3 pb-3">
            <div className="pb-2" style={{ fontWeight: 600, fontSize: '14px' }}>
              Obsoletas ({stale.length})
            </div>
            <div className="d-flex flex-column gap-2">
              {stale.map(item => (
                <Card key={item.intersection_id} style={{ borderLeft: '4px solid #DC3545' }}>
                  <Card.Body className="p-2">
                    <strong>Intersección {item.intersection_id}</strong>
                    <div style={{ fontSize: '13px' }}>
                      Polígonos {item.p_min} y {item.p_max} ya no se solapan
                      {item.overlap_area_actual === null
                        ? ' (algún polígono fue borrado/desactivado)'
                        : ''}
                      .
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        )}

        {duplicates.length > 0 && (
          <div className="px-3 pb-3">
            <div className="pb-2" style={{ fontWeight: 600, fontSize: '14px' }}>
              Duplicadas ({duplicates.length})
            </div>
            <div className="d-flex flex-column gap-2">
              {duplicates.map(item => (
                <Card
                  key={`${item.p_min}-${item.p_max}`}
                  style={{ borderLeft: '4px solid #6c757d' }}
                >
                  <Card.Body className="p-2">
                    <strong>
                      Polígonos {item.p_min} y {item.p_max}
                    </strong>
                    <div style={{ fontSize: '13px' }}>
                      Guardados {item.n_rows} veces (ids:{' '}
                      {item.intersection_ids})
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </div>
        )}
      </WrapperIntersections>
    </div>
  );
};
